import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocationContext } from './useLocationContext';
import { strategyApi } from '@/modules/strategy/api/strategyApi';
import { strategyKeys } from '@/modules/strategy/queryKeys';
import { BrandKitContext } from './useBrandKit';

const DIRECTION_NAMES: Record<string, string> = {
  warm: 'Warm & Familiar',
  trusted: 'Trusted & Established',
  clean: 'Modern & Clinical',
};

export function BrandKitProvider({ children }: { children: React.ReactNode }) {
  const { currentLocation } = useLocationContext();
  const locationId = currentLocation?.id ?? '';

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: strategyKeys.brandKit(locationId),
    queryFn: () => strategyApi.getBrandKit(locationId),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });

  const [selectedId, setSelectedIdState] = useState('warm');
  const [approved, setApproved] = useState(false);

  // Hydrate from backend when data loads
  useEffect(() => {
    if (data?.data) {
      if (data.data.direction) setSelectedIdState(data.data.direction);
      setApproved(!!data.data.approvedAt);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (direction: string) => strategyApi.saveBrandKit(locationId, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.brandKit(locationId) });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => strategyApi.clearBrandKit(locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.brandKit(locationId) });
    },
  });

  const setSelected = useCallback((id: string) => {
    setSelectedIdState(id);
    setApproved(false);
  }, []);

  const { mutate: saveKit } = saveMutation;
  const { mutate: clearKit } = clearMutation;

  const approve = useCallback(() => {
    setApproved(true);
    if (locationId) saveKit(selectedId);
  }, [locationId, selectedId, saveKit]);

  const unapprove = useCallback(() => {
    setApproved(false);
    if (locationId) clearKit();
  }, [locationId, clearKit]);

  const kitName = DIRECTION_NAMES[selectedId] ?? selectedId;

  const value = useMemo(
    () => ({ approved, selectedId, kitName, isLoading, setSelected, approve, unapprove }),
    [approved, selectedId, kitName, isLoading, setSelected, approve, unapprove],
  );

  return <BrandKitContext.Provider value={value}>{children}</BrandKitContext.Provider>;
}
